/**
 * Behavioral Questions Bank - Banco de Questões Comportamentais DISC
 * Com seleção de perguntas para avaliação
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { discAPI } from '../../lib/api';
import { useFeatureAccess, BlockedFeatureOverlay } from '../../components/FeatureGate';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../components/ui/alert';
import { 
  Brain,
  Plus,
  Loader2,
  Search,
  AlertTriangle,
  CheckCircle2,
  Globe,
  Building2,
  HelpCircle,
  Filter,
  Database,
  Check,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

// DISC category info
const DISC_CATEGORIES = {
  D: { name: 'Dominância', color: 'bg-red-100 text-red-700 border-red-200', description: 'Foco em resultados e desafios', bgLight: 'bg-red-50' },
  I: { name: 'Influência', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', description: 'Foco em pessoas e comunicação', bgLight: 'bg-yellow-50' },
  S: { name: 'Estabilidade', color: 'bg-green-100 text-green-700 border-green-200', description: 'Foco em cooperação e consistência', bgLight: 'bg-green-50' },
  C: { name: 'Conformidade', color: 'bg-blue-100 text-blue-700 border-blue-200', description: 'Foco em qualidade e precisão', bgLight: 'bg-blue-50' }
};

const NIVEIS = [
  { value: 'estagiario', label: 'Estagiário' },
  { value: 'junior', label: 'Júnior' },
  { value: 'pleno', label: 'Pleno' },
  { value: 'senior', label: 'Sênior' },
  { value: 'coordenador', label: 'Coordenador' },
  { value: 'gerente', label: 'Gerente' },
  { value: 'diretor', label: 'Diretor' }
];

export default function BehavioralQuestionsBank() {
  const { hasAccess, loading: featureLoading } = useFeatureAccess('banco_questoes_disc');
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [activeCategory, setActiveCategory] = useState('D');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectionStats, setSelectionStats] = useState({ D: 0, I: 0, S: 0, C: 0, total: 0 });
  
  // New question form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    pergunta: '',
    categoria_disc: 'D',
    nivel_hierarquico: ''
  });

  useEffect(() => {
    if (hasAccess) {
      fetchQuestions();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  useEffect(() => {
    calculateSelectionStats();
  }, [questions]);

  // Check feature access
  if (!hasAccess && !featureLoading && !loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <BlockedFeatureOverlay 
          feature="banco_questoes_disc"
          title="Banco de Questões DISC Bloqueado"
          description="O acesso ao Banco de Questões Comportamentais DISC não está liberado para sua conta. Entre em contato com o administrador para solicitar acesso a esta funcionalidade premium."
        />
      </DashboardLayout>
    );
  }

  const fetchQuestions = async () => {
    try {
      const response = await discAPI.getBehavioralQuestions({});
      setQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.error('Erro ao carregar questões');
    } finally {
      setLoading(false);
    }
  };

  const calculateSelectionStats = () => {
    const stats = { D: 0, I: 0, S: 0, C: 0, total: 0 };
    questions.forEach(q => {
      if (q.is_selected) {
        stats[q.categoria_disc] = (stats[q.categoria_disc] || 0) + 1;
        stats.total++;
      }
    });
    setSelectionStats(stats);
  };

  const handleSeedQuestions = async () => {
    setSeeding(true);
    try {
      const response = await discAPI.seedQuestions();
      toast.success(response.data.message);
      fetchQuestions();
    } catch (error) {
      toast.error('Erro ao criar perguntas padrão');
    } finally {
      setSeeding(false);
    }
  };

  const handleToggleSelection = async (questionId) => {
    try {
      const response = await discAPI.toggleQuestionSelection(questionId);
      // Update local state
      setQuestions(prev => prev.map(q => 
        q.id === questionId ? { ...q, is_selected: response.data.is_selected } : q
      ));
      toast.success(response.data.is_selected ? 'Pergunta selecionada!' : 'Pergunta removida da seleção');
    } catch (error) {
      toast.error('Erro ao atualizar seleção');
    }
  };

  const handleCreateQuestion = async () => {
    if (!newQuestion.pergunta.trim()) {
      toast.error('Digite a pergunta');
      return;
    }

    setSaving(true);
    try {
      const response = await discAPI.createBehavioralQuestion({
        pergunta: newQuestion.pergunta,
        categoria_disc: newQuestion.categoria_disc,
        nivel_hierarquico: newQuestion.nivel_hierarquico || null
      });
      
      if (response.data.moderation?.flagged) {
        toast.warning('Pergunta salva apenas no banco da empresa (contém termos sensíveis)');
      } else {
        toast.success('Pergunta adicionada ao banco!');
      }
      
      await fetchQuestions();
      setNewQuestion({ pergunta: '', categoria_disc: 'D', nivel_hierarquico: '' });
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pergunta');
    } finally {
      setSaving(false);
    }
  };

  const getFilteredQuestions = (category) => {
    return questions
      .filter(q => q.categoria_disc === category)
      .filter(q => !searchTerm || q.pergunta.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="behavioral-questions-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Banco de Questões DISC</h1>
              <p className="text-slate-600">Selecione as perguntas para compor suas avaliações</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {questions.length === 0 && (
              <Button
                variant="outline"
                onClick={handleSeedQuestions}
                disabled={seeding}
                data-testid="seed-questions-btn"
              >
                {seeding ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Database className="w-4 h-4 mr-2" />
                )}
                Carregar Perguntas Padrão
              </Button>
            )}
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-cyan-600 hover:bg-cyan-700" data-testid="add-question-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Pergunta
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Adicionar Pergunta</DialogTitle>
                  <DialogDescription>
                    Crie uma nova pergunta comportamental para o banco.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Pergunta</Label>
                    <Textarea
                      value={newQuestion.pergunta}
                      onChange={(e) => setNewQuestion({ ...newQuestion, pergunta: e.target.value })}
                      placeholder="Ex: Como você lida com prazos apertados?"
                      rows={3}
                      data-testid="question-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Categoria DISC</Label>
                      <Select
                        value={newQuestion.categoria_disc}
                        onValueChange={(v) => setNewQuestion({ ...newQuestion, categoria_disc: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DISC_CATEGORIES).map(([key, cat]) => (
                            <SelectItem key={key} value={key}>
                              {key} - {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Nível (opcional)</Label>
                      <Select
                        value={newQuestion.nivel_hierarquico || "todos"}
                        onValueChange={(v) => setNewQuestion({ ...newQuestion, nivel_hierarquico: v === "todos" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos os níveis</SelectItem>
                          {NIVEIS.map(nivel => (
                            <SelectItem key={nivel.value} value={nivel.value}>
                              {nivel.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {newQuestion.categoria_disc && (
                    <div className={`p-3 rounded-lg border ${DISC_CATEGORIES[newQuestion.categoria_disc].color}`}>
                      <p className="text-sm font-medium">{DISC_CATEGORIES[newQuestion.categoria_disc].name}</p>
                      <p className="text-xs opacity-80">{DISC_CATEGORIES[newQuestion.categoria_disc].description}</p>
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button 
                    onClick={handleCreateQuestion}
                    disabled={saving || !newQuestion.pergunta.trim()}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                    Adicionar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Selection Summary */}
        <Alert className="border-cyan-200 bg-cyan-50">
          <Info className="w-4 h-4 text-cyan-600" />
          <AlertTitle className="text-cyan-800">Perguntas Selecionadas para Avaliação</AlertTitle>
          <AlertDescription className="text-cyan-700">
            <div className="flex flex-wrap gap-4 mt-2">
              <span className="font-medium">Total: {selectionStats.total}</span>
              <Separator orientation="vertical" className="h-5" />
              {Object.entries(DISC_CATEGORIES).map(([key, cat]) => (
                <span key={key} className={`px-2 py-0.5 rounded ${cat.color}`}>
                  {key}: {selectionStats[key] || 0}
                </span>
              ))}
            </div>
            <p className="text-xs mt-2 text-cyan-600">
              Selecione as perguntas que deseja usar nas avaliações DISC. Recomendamos pelo menos 5 perguntas por categoria.
            </p>
          </AlertDescription>
        </Alert>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar pergunta..."
            className="pl-10"
            data-testid="search-questions"
          />
        </div>

        {/* Empty State */}
        {questions.length === 0 && (
          <Card className="border-dashed border-2 border-slate-300">
            <CardContent className="p-12 text-center">
              <Database className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">Banco de Perguntas Vazio</h3>
              <p className="text-slate-500 mb-4">
                Clique no botão "Carregar Perguntas Padrão" para adicionar 40 perguntas pré-definidas (10 por categoria DISC).
              </p>
              <Button onClick={handleSeedQuestions} disabled={seeding} className="bg-cyan-600 hover:bg-cyan-700">
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
                Carregar Perguntas Padrão
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Questions by Category */}
        {questions.length > 0 && (
          <Tabs value={activeCategory} onValueChange={setActiveCategory}>
            <TabsList className="grid w-full grid-cols-4">
              {Object.entries(DISC_CATEGORIES).map(([key, cat]) => (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className={`flex items-center gap-2 data-[state=active]:${cat.bgLight}`}
                >
                  <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${cat.color}`}>
                    {key}
                  </span>
                  <span className="hidden sm:inline">{cat.name}</span>
                  <Badge variant="secondary" className="ml-1">
                    {getFilteredQuestions(key).length}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.keys(DISC_CATEGORIES).map((category) => (
              <TabsContent key={category} value={category} className="mt-4">
                <Card className={`border-2 ${DISC_CATEGORIES[category].color.split(' ')[2]}`}>
                  <CardHeader className={DISC_CATEGORIES[category].bgLight}>
                    <CardTitle className="flex items-center gap-2">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold ${DISC_CATEGORIES[category].color}`}>
                        {category}
                      </span>
                      {DISC_CATEGORIES[category].name}
                    </CardTitle>
                    <CardDescription>{DISC_CATEGORIES[category].description}</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {getFilteredQuestions(category).length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        <Brain className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        <p>Nenhuma pergunta encontrada nesta categoria</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {getFilteredQuestions(category).map((question, index) => (
                          <div
                            key={question.id}
                            className={`flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors ${
                              question.is_selected ? 'bg-green-50' : ''
                            }`}
                          >
                            <Checkbox
                              checked={question.is_selected || false}
                              onCheckedChange={() => handleToggleSelection(question.id)}
                              className="mt-1 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                              data-testid={`select-question-${question.id}`}
                            />
                            
                            <div className="flex-1">
                              <p className={`text-slate-800 ${question.is_selected ? 'font-medium' : ''}`}>
                                {index + 1}. {question.pergunta}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                {question.nivel_hierarquico && (
                                  <Badge variant="outline" className="text-xs">
                                    {question.nivel_hierarquico}
                                  </Badge>
                                )}
                                {question.is_global ? (
                                  <Badge variant="outline" className="text-xs text-green-700 bg-green-50 border-green-200">
                                    <Globe className="w-3 h-3 mr-1" />
                                    Padrão
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs text-blue-700 bg-blue-50 border-blue-200">
                                    <Building2 className="w-3 h-3 mr-1" />
                                    Personalizada
                                  </Badge>
                                )}
                                {question.is_selected && (
                                  <Badge className="text-xs bg-green-600">
                                    <Check className="w-3 h-3 mr-1" />
                                    Selecionada
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Help Card */}
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <HelpCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">Como funciona?</p>
                <ul className="list-disc list-inside space-y-1 text-slate-500">
                  <li>Selecione as perguntas que deseja usar nas avaliações DISC</li>
                  <li>As perguntas selecionadas serão usadas no questionário de candidatos e funcionários</li>
                  <li>Você pode adicionar suas próprias perguntas personalizadas</li>
                  <li>Recomendamos pelo menos 5 perguntas por categoria para uma avaliação completa</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
